import _groupBy from 'lodash/groupBy';
import _sortBy from 'lodash/sortBy';
import moment from 'moment-timezone';
import { baseDataZoom, CustomLineStyleRender } from 'volta/components/bv-chart';
import { dateFormatter, FrequencyDict } from 'volta/utils/chart-utils';

import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { EChartsOption, ISelectLegendArgs } from 'volta/components/bv-chart';
import type { IDemandForecastAgg } from 'volta/models/types/forecasts';
import type { TAduFrequency } from 'volta/models/types/planning-settings';
import type IntlService from 'ember-intl/services/intl';

interface IArgs {
  data?: IDemandForecastAgg[];
  frequency: TAduFrequency;
  seriesColors?: Record<string, { css: string; rgb: string }>;
  onLegendSelect?: (forecastTypes: string[]) => void;
}

const dateFromForecast = (f: IDemandForecastAgg, frequency: TAduFrequency) =>
  moment
    .utc(frequency === 'MONTHLY' ? f.month : frequency === 'WEEKLY' ? f.week : f.date)
    .toDate()
    .getTime();

const demandFromForecast = (f: IDemandForecastAgg, frequency: TAduFrequency) =>
  frequency === 'MONTHLY'
    ? f.monthlyDemandSum
    : frequency === 'WEEKLY'
      ? f.weeklyDemandSum
      : f.dailyDemand;

const outgoingFromForecast = (f: IDemandForecastAgg, frequency: TAduFrequency) =>
  frequency === 'MONTHLY'
    ? f.monthlyOutgoingSum
    : frequency === 'WEEKLY'
      ? f.weeklyOutgoingSum
      : f.outgoing;

export default class ForecastsChart extends Component<IArgs> {
  @service intl!: IntlService;

  @tracked selected?: Record<string, boolean>;

  get frequency() {
    return this.args.frequency ?? 'WEEKLY';
  }

  get period() {
    return FrequencyDict[this.frequency].period;
  }

  get today() {
    return moment.utc().startOf(this.period).toDate().toISOString();
  }

  get series() {
    return _groupBy(this.args.data ?? [], (f) => f.forecastType);
  }

  get forecastTypes() {
    return Object.keys(this.series).sort();
  }

  get transactions(): [number, number][] {
    const { frequency } = this;
    const viewed = new Set();
    return _sortBy(
      (this.args.data ?? []).reduce((acc, d) => {
        const date = dateFromForecast(d, frequency);
        if (!viewed.has(date) && moment.utc(d.date).isBefore(moment.utc(), 'd')) {
          acc.push([date, outgoingFromForecast(d, frequency)]);
        }
        viewed.add(date);
        return acc;
      }, [] as [number, number][]),
      (t) => t[0]
    );
  }

  get forecastsData(): Record<string, [number, number][]> {
    const { series, frequency } = this;

    return Object.entries(series).reduce((acc, [label, d]) => {
      const viewed = new Set();
      const ftData: [number, number][] = [];

      d.forEach((forecast) => {
        const date = dateFromForecast(forecast, frequency);
        if (!viewed.has(date)) {
          ftData.push([date, demandFromForecast(forecast, frequency)]);
          viewed.add(date);
        }
      });

      acc[label] = _sortBy(ftData, (d) => d[0]);
      return acc;
    }, {} as Record<string, [number, number][]>);
  }

  @cached
  get chartOptions(): EChartsOption {
    const { forecastTypes } = this;

    const forecastsSeries: EChartsOption[] = Object.entries(this.forecastsData).map(
      ([name, data]) => {
        return {
          name,
          data,
          type: 'custom',
          color: this.args.seriesColors?.[name].rgb,
          renderItem: CustomLineStyleRender((_series, _x1, _y1, x2, _y2) => {
            const isFuture = moment.utc(x2).isAfter(moment.utc());
            return {
              lineDash: [isFuture ? 4 : 0]
            };
          }),
          showSymbol: false,
          z: 3
        } as EChartsOption;
      }
    );

    const label = this.intl.t('outgoing');
    const color = this.args.seriesColors?.[label].rgb;
    const areaStyle = { color, opacity: 0.4 };

    const transactionsSeries: EChartsOption[] = [
      {
        name: label,
        data: [...this.transactions],
        type: 'line',
        step: 'middle',
        showSymbol: false,
        color,
        areaStyle
      } as EChartsOption
    ];

    const dates = this.args.data?.map((d) => d.date)?.sort() ?? [];
    const series: EChartsOption[] = forecastsSeries.concat(transactionsSeries);
    series.push({
      name: 'today',
      type: 'line',
      markLine: {
        symbol: 'none',
        label: {
          formatter: () => dateFormatter(this.today, this.frequency)
        },
        data: [
          {
            silent: true,
            xAxis: this.today
          },
          {
            silent: true,
            xAxis: this.today
          }
        ]
      }
    } as EChartsOption);

    return {
      legend: {
        left: 0,
        top: 0,
        data: [{ name: this.intl.t('outgoing'), icon: 'roundRect' }].concat(
          forecastTypes.map((ft) => ({ name: ft, icon: 'emptyCircle' }))
        ),
        selected: this.selected
      },
      xAxis: {
        data: dates,
        axisLabel: {
          formatter: (value: moment.MomentInput) => dateFormatter(value, this.frequency)
        }
      },
      dataZoom: baseDataZoom(),
      grid: {
        left: 0,
        right: 0,
        containLabel: true
      },
      series
    } as EChartsOption;
  }

  @action
  handleSelected({ selected }: ISelectLegendArgs) {
    this.selected = selected;
    const forecastTypes = Object.entries(selected)
      .reduce((fts, [ft, isSelected]) => {
        if (isSelected) {
          fts.push(ft);
        }
        return fts;
      }, [] as string[]);
    const outgoing = this.intl.t('outgoing');
    this.args.onLegendSelect?.(forecastTypes.filter(ft => ft !== outgoing));
  }
}
